import { action } from '@ember/object';
import { debounce } from '@ember/runloop';

import type { SVGPathArray } from 'highcharts';

import { AppEvent } from 'webapp/enums/events';

import type DChart from 'webapp/d-chart/d-chart';
import type { HighchartsChartWithCustomPlotlines } from 'webapp/d-chart/renderer/base';
import LineOrBarChartRenderer from 'webapp/d-chart/renderer/line-or-bar-chart';

// This is renderer for Forecast detail page chart
// Note that Forecast type series are not handled here
// bcos they should be supported also on normal blocks ootb

const classNameBase = 'forecast-plot-line';
export const historyPlotlineClassName = `${classNameBase}--h`;
const forecastPlotlineClassName = `${classNameBase}--f`;
export const forecastPlotlineClassNameToPoint = `${forecastPlotlineClassName}-to-point`;

export default class ForecastChartRenderer extends LineOrBarChartRenderer {
	constructor(chart: DChart) {
		super(chart);
	}

	override get visualization() {
		return super.visualization;
	}

	get mainSeries() {
		// assume only one main
		return this.series.find((s) => s.isMain);
	}

	protected override onChartRender(hChart: Highcharts.Chart) {
		super.onChartRender(hChart);
		this.updatePlotLines(hChart as HighchartsChartWithCustomPlotlines);
		debounce(this.triggerChartRendered, 500);
	}

	private updatePlotLines(hChart: HighchartsChartWithCustomPlotlines) {
		const xAxis = hChart.xAxis[0]!,
			yAxis = hChart.yAxis[0]!;

		// plotlines, but only to the point height
		if (!hChart.customPlotLines) {
			hChart.customPlotLines = [];
		}

		const usedPlotLines: Highcharts.SVGElement[] = [];

		const topPos = yAxis.toPixels(yAxis.max || 0, false),
			bottomPos = yAxis.toPixels(yAxis.min || 0, false);

		const handlePlotline = (className: string, posX: number, pointValuePos: number) => {
			let plotLine = hChart.customPlotLines.find((p) => {
				return p.attr('class') === className;
			});
			const pathD: SVGPathArray = [['M', posX, bottomPos], ['V', pointValuePos], ['z']];
			if (plotLine) {
				plotLine.animate({
					d: pathD,
				});
			} else {
				plotLine = hChart.renderer
					.path(pathD)
					.attr({
						zIndex: 4,
					})
					.addClass(className)
					.add();
			}
			return plotLine;
		};

		const isSeriesVisibleInChart = (idForHc: string): boolean => {
			const hcSeries = hChart.series.find((s) => s.options.id === idForHc);
			return hcSeries?.visible ?? true;
		};

		// adding plotline for last current value
		const mainSeries = this.mainSeries;
		if (mainSeries && isSeriesVisibleInChart(mainSeries.idForHc)) {
			const mainPoint = mainSeries.getLastPointWithValue();
			if (mainPoint) {
				const plotLine = handlePlotline(
					`${classNameBase} ${historyPlotlineClassName}`,
					xAxis.toPixels(mainPoint.index, false),
					yAxis.toPixels(mainPoint.value!, false),
				);
				usedPlotLines.push(plotLine);
			}
		}

		const mainForecastSeries = this.series.find((s) => s.isForecast);
		if (mainForecastSeries && isSeriesVisibleInChart(mainForecastSeries.idForHc)) {
			const forecastLastPoint = mainForecastSeries.getLastPointWithValue();
			if (forecastLastPoint) {
				// adding plotline for last forecast value (full height)
				const plotLineFull = handlePlotline(
					`${classNameBase} ${forecastPlotlineClassName}`,
					xAxis.toPixels(forecastLastPoint.index, false),
					topPos,
				);
				usedPlotLines.push(plotLineFull);

				// adding "fake" plotline for last forecast value (only to point - used for positioning static tooltip)
				const plotLine = handlePlotline(
					`${classNameBase} ${forecastPlotlineClassNameToPoint}`,
					xAxis.toPixels(forecastLastPoint.index, false),
					yAxis.toPixels(forecastLastPoint.value!, false),
				);
				usedPlotLines.push(plotLine);
			}
		}

		// remove unused plotlines
		hChart.customPlotLines.forEach((pl) => {
			if (!usedPlotLines.includes(pl)) {
				pl.destroy();
			}
		});
		hChart.customPlotLines = usedPlotLines;
	}

	@action
	private triggerChartRendered() {
		this.eventManagerService.trigger(AppEvent.FORECAST_CHART_RENDERED);
	}
}
